import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { formatCurrency } from "../../utils/formatCurrency";
import { getLayoutById } from "../../services/api";
import { formatUrl } from "../../utils/config";

const OfferCard = ({ item, index }) => {
  const [layoutName, setLayoutName] = useState(null);

  useEffect(() => {
    const fetchLayoutName = async () => {
      if (item.propertyType === "PLOT" && item.plot?.layoutId) {
        try {
          const response = await getLayoutById(item.plot.layoutId);
          if (response.success && response.payload) {
            setLayoutName(
              response.payload.name ||
                response.payload.title ||
                `Layout ${item.plot.layoutId}`
            );
          } else {
            setLayoutName(`Layout ${item.plot.layoutId}`);
          }
        } catch (error) {
          console.error("Failed to fetch layout name:", error);
          setLayoutName(`Layout ${item.plot.layoutId}`);
        }
      }
    };

    fetchLayoutName();
  }, [item.plot?.layoutId, item.propertyType]);

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, delay: index * 0.2 },
    },
  };

  // Determine if this is a house or plot offer
  const isHouseOffer = item.propertyType === "HOUSE";
  const isPlotOffer = item.propertyType === "PLOT";

  // Get the appropriate property data
  const property = isHouseOffer ? item.house : item.plot;

  // Get thumbnail image
  const getThumbnail = () => {
    if (isHouseOffer && item.house?.thumbnail) {
      return item.house.thumbnail;
    } else if (
      isPlotOffer &&
      item.plot?.photos &&
      item.plot.photos.length > 0
    ) {
      return item.plot.photos[0];
    }
    return "/assets/img/default-offer.png";
  };

  // Get location string
  const getLocation = () => {
    if (isHouseOffer && item.house) {
      return `${item.house.district || ""} ${item.house.lga} ${
        item.house.state
      }`;
    } else if (isPlotOffer && item.plot) {
      return `${item.plot.district || ""} ${item.plot.lga} ${item.plot.state}`;
    }
    return item.propertyLocation || "Location not specified";
  };

  // Get property details
  const getPropertyDetails = () => {
    if (isHouseOffer && item.house) {
      return [
        { icon: "🛏", value: item.house.bedRooms },
        { icon: "🚽", value: item.house.toilets },
        { icon: "📏", value: `${item.house.sizeSqm} SQM` },
      ];
    } else if (isPlotOffer && item.plot) {
      return [
        { icon: "📏", value: `${item.plot.sizeSqm} SQM` },
        { icon: "🏗", value: item.plot.locationType },
        { icon: "📍", value: `Plot ${item.plot.plotNumber}` },
      ];
    }
    return [];
  };

  // Get offer end date
  const getOfferEndDate = () => {
    if (item.endDate) {
      const endDate = new Date(item.endDate);
      const now = new Date();
      const timeLeft = endDate - now;

      if (timeLeft <= 0) {
        return "Offer Expired";
      }

      const daysLeft = Math.ceil(timeLeft / (1000 * 60 * 60 * 24));
      if (daysLeft === 1) {
        return "Ends Today";
      } else if (daysLeft <= 7) {
        return `Ends in ${daysLeft} days`;
      } else {
        return `Ends ${endDate.toLocaleDateString()}`;
      }
    }
    return null;
  };

  // Get user-friendly offer type label
  // const getOfferTypeLabel = () => {
  //   switch (item.offerType) {
  //     case 'DISCOUNT_PERCENTAGE':
  //       return 'Percentage Discount';
  //     case 'DISCOUNT_AMOUNT':
  //       return 'Fixed Discount';
  //     case 'BONUS':
  //       return 'Bonus Offer';
  //     case 'FREE_ITEM':
  //       return 'Free Item';
  //     default:
  //       return item.offerType || 'Special Offer';
  //   }
  // };

  // Get price display
  const getPriceDisplay = () => {
    if (item.priceRange) {
      return item.priceRange;
    }

    if (isPlotOffer && item.plot?.price) {
      const price = parseFloat(item.plot.price);
      if (item.discountPercentage) {
        const discountedPrice = price * (1 - item.discountPercentage / 100);
        return {
          originalPrice: formatCurrency(price),
          discountedPrice: formatCurrency(discountedPrice),
          hasDiscount: true,
        };
      }
      return {
        originalPrice: formatCurrency(price),
        discountedPrice: null,
        hasDiscount: false,
      };
    }

    if (isHouseOffer && item.house?.price) {
      const price = parseFloat(item.house.price);
      if (item.discountPercentage) {
        const discountedPrice = price * (1 - item.discountPercentage / 100);
        return {
          originalPrice: formatCurrency(price),
          discountedPrice: formatCurrency(discountedPrice),
          hasDiscount: true,
        };
      }
      return {
        originalPrice: formatCurrency(price),
        discountedPrice: null,
        hasDiscount: false,
      };
    }

    return {
      originalPrice: "Price on request",
      discountedPrice: null,
      hasDiscount: false,
    };
  };

  return (
    <motion.div
      className="col-lg-4 col-md-6"
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      style={{
        position: "relative",
      }}
    >
      <style>
        {`
          .offer-card-hover:hover .project-card-details h4,
          .offer-card-hover:hover .project-card-details p,
          .offer-card-hover:hover .project-card-details span,
          .offer-card-hover:hover .project-card-details .layout-name,
          .offer-card-hover:hover .project-card-details .offer-end-date,
          .offer-card-hover:hover .project-card-details .th-btn {
            color: white !important;
          }
          
          .offer-card-hover:hover .project-card-details .th-btn {
            background-color: white !important;
            color: #333 !important;
          }
        `}
      </style>

      <div
        className="project-card style1-1 hover-card offer-card-hover"
        style={{
          transition: "all 0.3s ease",
          cursor: "pointer",
        }}
      >
        <div className="project-card-img">
          <img
            src={formatUrl(getThumbnail())}
            alt={item.title}
            className="w-100"
            onError={(e) => (e.target.src = "/assets/img/default-offer.png")}
          />
          <div className="card-badge">
            {/* <span className={`status ${item.status.toLowerCase()}`}>
              {item.status}
            </span> */}
            {/* <span className="type">{getOfferTypeLabel()}</span> */}
          </div>
        </div>
        <div
          className="project-card-details"
          style={{
            transition: "all 0.3s ease",
          }}
        >
          <h4 className="box-title">
            <Link
              to={`/offers/${item.id}`}
              state={{ offerData: item }}
              style={{
                color: "inherit",
                textDecoration: "none",
                transition: "color 0.3s ease",
              }}
            >
              {item.title}
            </Link>
          </h4>
          <p className="location" style={{ transition: "color 0.3s ease" }}>
            📍 {getLocation()}
          </p>
          {/* {layoutName && (
            <p className="layout-name" style={{ 
              color: '#666', 
              fontSize: '0.9em', 
              marginBottom: '8px',
              transition: 'color 0.3s ease'
            }}>
              🏗️ {layoutName}
            </p>
          )} */}
          {getOfferEndDate() && (
            <p
              className="offer-end-date"
              style={{
                color: "#e74c3c",
                fontSize: "0.9em",
                marginBottom: "8px",
                fontWeight: "bold",
                transition: "color 0.3s ease",
              }}
            >
              ⏰ {getOfferEndDate()}
            </p>
          )}
          <div className="price-display">
            {(() => {
              const priceInfo = getPriceDisplay();
              if (priceInfo.hasDiscount) {
                return (
                  <p
                    className="price"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      flexWrap: "wrap",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <span
                      style={{
                        textDecoration: "line-through",
                        color: "#999",
                        transition: "color 0.3s ease",
                      }}
                    >
                      {priceInfo.originalPrice}
                    </span>
                    <span
                      style={{
                        color: "#666",
                        transition: "color 0.3s ease",
                      }}
                    >
                      →
                    </span>
                    <span
                      style={{
                        color: "#e74c3c",
                        fontWeight: "bold",
                        transition: "color 0.3s ease",
                      }}
                    >
                      {priceInfo.discountedPrice}
                    </span>
                  </p>
                );
              } else {
                return (
                  <p
                    className="price"
                    style={{
                      fontWeight: "bold",
                      transition: "color 0.3s ease",
                    }}
                  >
                    {priceInfo.originalPrice}
                  </p>
                );
              }
            })()}
          </div>
          {item.discountPercentage && (
            <p className="discount" style={{ transition: "color 0.3s ease" }}>
              {item.discountPercentage}% OFF
            </p>
          )}
          <div
            className="property-info"
            style={{ transition: "color 0.3s ease" }}
          >
            {getPropertyDetails().map((detail, idx) => (
              <span key={idx} style={{ transition: "color 0.3s ease" }}>
                {detail.icon} {detail.value}
              </span>
            ))}
          </div>
          <Link
            to={`/offers/${item.id}`}
            state={{ offerData: item }}
            className="th-btn style9 mt-10"
            style={{
              transition: "all 0.3s ease",
            }}
          >
            View Details <i className="fas fa-arrow-right ms-1" />
          </Link>
        </div>
      </div>
    </motion.div>
  );
};

export default OfferCard;
